/**
 * Constants for API configuration
 */
export const API_CONSTANTS = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000, // Base delay in ms
  CHUNK_SIZE: 50,
  TOKEN_KEY: 'token',
};

/**
 * Helper to retry failed requests with exponential backoff
 * @param fn Function to retry
 * @param maxRetries Maximum number of retry attempts
 * @param baseDelay Base delay in ms between retries
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = API_CONSTANTS.MAX_RETRIES,
  baseDelay: number = API_CONSTANTS.RETRY_DELAY
): Promise<T> => {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn(); // Attempt the function call
    } catch (error) {
      lastError = error as Error; // Store the error
      // If not the last attempt, wait before retrying
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff
        await new Promise((resolve) => setTimeout(resolve, delay));
        console.log(`Retrying request (attempt ${attempt + 2}/${maxRetries})...`);
        continue; // Continue to the next attempt
      }
    }
  }
  // If all retries fail, throw the last encountered error
  throw lastError || new Error('All retry attempts failed');
};

/**
 * Extracts error message from API errors
 * @param error Error object from API call
 */
export const extractErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  } else if (error?.message) {
    return error.message;
  }
  return 'An unknown error occurred';
};